<script setup lang="ts">
/**
 * A 3D scene (three.js, 2026-10-12), decorative and silent for assistive technology. three.js is its
 * own chunk, loaded only by the pages that show one (nothing from another site: the security policy
 * forbids it). The scene pauses off screen and when the tab is hidden, draws one still frame when
 * animations are reduced or off, shows the `fallback` slot where WebGL is missing or lost, follows
 * the theme and the company's colour, and gives its graphics memory back when the page goes.
 */
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { WebGLRenderer } from 'three'
import { motion } from '@/lib/motion'
import { readPalette } from './palette'
import type { SceneFactory, SceneHandle } from './types'

const p = withDefaults(defineProps<{
  scene: () => Promise<SceneFactory>
  options?: Record<string, unknown>
  /** At most this many frames a second (a TV: 30). */
  fps?: number
  /** The scene follows the pointer over the page (a slight parallax). */
  pointer?: boolean
}>(), { fps: 60 })

const host = ref<HTMLDivElement | null>(null)
const canvas = ref<HTMLCanvasElement | null>(null)
const failed = ref(false)
const ready = ref(false)
let renderer: WebGLRenderer | null = null
let handle: SceneHandle | null = null
let raf = 0
let last = 0
/** The scene's clock at its last frame (seconds): a frame drawn out of the loop shows the same moment. */
let clock = 0
let onScreen = true
let gone = false
const pointer = { x: 0, y: 0 }
const cleanups: Array<() => void> = []

function render(time: number, delta: number) {
  if (!handle || !renderer) return
  clock = time
  handle.frame(time, delta)
  renderer.render(handle.scene, handle.camera)
}
function loop(now: number) {
  raf = requestAnimationFrame(loop)
  if (document.hidden || !onScreen) { last = 0; return }
  if (last && now - last < 1000 / p.fps - 2) return
  const delta = last ? Math.min(0.1, (now - last) / 1000) : 0
  last = now
  render(now / 1000, delta)
}
const stop = () => { cancelAnimationFrame(raf); raf = 0 }
/* Moving when everything may move, else one still frame. */
function start() {
  stop()
  if (motion.value === 'full') { last = 0; raf = requestAnimationFrame(loop) } else render(0, 0)
}
const redraw = () => { if (!raf) render(0, 0) }

function repaint() {
  if (!handle?.repaint) return
  handle.repaint(readPalette())
  redraw()
}

onMounted(async () => {
  try {
    const [THREE, factory] = await Promise.all([import('three'), p.scene()])
    if (gone || !host.value || !canvas.value) return
    renderer = new THREE.WebGLRenderer({ canvas: canvas.value, antialias: true, alpha: true, powerPreference: 'low-power' })
    renderer.setClearColor(0x000000, 0)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75))
    const box = host.value.getBoundingClientRect()
    renderer.setSize(Math.max(1, box.width), Math.max(1, box.height), false)
    handle = factory({ three: THREE, width: Math.max(1, box.width), height: Math.max(1, box.height), palette: readPalette(), pointer,
      canvas: canvas.value }, p.options ?? {})

    // Resizing clears the canvas after this frame's drawing: drawn again at once, else it shows blank until the
    // next frame, and all along a transition of the page's width (a panel docking at its side) every frame does.
    const ro = new ResizeObserver(([e]) => {
      const w = Math.max(1, e!.contentRect.width), h = Math.max(1, e!.contentRect.height)
      renderer?.setSize(w, h, false)
      handle?.resize(w, h)
      render(clock, 0)
    })
    ro.observe(host.value)
    const io = new IntersectionObserver(([e]) => { onScreen = Boolean(e?.isIntersecting) })
    io.observe(host.value)
    // The theme, or the company's colour set on the page.
    const mo = new MutationObserver(repaint)
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'style', 'class'] })
    const dark = window.matchMedia?.('(prefers-color-scheme: dark)')
    dark?.addEventListener('change', repaint)
    const lost = (e: Event) => { e.preventDefault(); failed.value = true; stop() }
    canvas.value.addEventListener('webglcontextlost', lost)
    cleanups.push(() => { ro.disconnect(); io.disconnect(); mo.disconnect(); dark?.removeEventListener('change', repaint) })
    if (p.pointer) {
      const move = (e: PointerEvent) => {
        const r = host.value?.getBoundingClientRect()
        if (!r) return
        pointer.x = Math.max(-1, Math.min(1, ((e.clientX - r.left) / r.width) * 2 - 1))
        pointer.y = Math.max(-1, Math.min(1, ((e.clientY - r.top) / r.height) * 2 - 1))
      }
      window.addEventListener('pointermove', move, { passive: true })
      cleanups.push(() => window.removeEventListener('pointermove', move))
    }
    start()
    ready.value = true
  } catch {
    // No WebGL here (an old workstation, a remote desktop): the still picture instead.
    failed.value = true
  }
})

watch(motion, () => { if (handle) start() })
watch(() => p.options, (o) => { handle?.update?.(o ?? {}); redraw() }, { deep: true })

onBeforeUnmount(() => {
  gone = true
  stop()
  for (const c of cleanups) c()
  handle?.dispose()
  if (renderer) { renderer.dispose(); renderer.forceContextLoss() }
  handle = null
  renderer = null
})
</script>

<template>
  <!-- Its place and size are the page's (`absolute inset-0`, `h-36`…): no position of its own to fight them. -->
  <div ref="host" class="overflow-hidden" aria-hidden="true">
    <div class="relative h-full w-full">
      <canvas v-show="!failed" ref="canvas"
              :class="['absolute inset-0 block h-full w-full transition-opacity duration-slowest ease-out', ready ? 'opacity-100' : 'opacity-0']" />
      <div v-if="failed" class="absolute inset-0"><slot name="fallback" /></div>
    </div>
  </div>
</template>
